import { questions, CATEGORIES } from '../data/questions';

const MAX_SCORE = 100;

/**
 * answers: { [questionId]: number | number[] }
 *   - single-select -> selected option index (0-based)
 *   - multi-select   -> array of selected option indices
 *
 * Returns: { categoryScores: { [categoryKey]: 0-100 }, overallScore: 0-100 }
 */
export function calculateScores(answers) {
  const totals = {};
  CATEGORIES.forEach((c) => {
    totals[c.key] = { weighted: 0, weightSum: 0 };
  });

  questions.forEach((q) => {
    if (q.personalizationOnly) return;
    const answer = answers[q.id];
    if (answer === undefined || answer === null) return;

    let valuePercent = 0;
    if (q.type === 'multi') {
      const selected = Array.isArray(answer) ? answer : [];
      valuePercent = (selected.length / q.options.length) * 100;
    } else {
      valuePercent = (answer / (q.options.length - 1)) * 100;
    }

    Object.entries(q.weights || {}).forEach(([category, weight]) => {
      totals[category].weighted += valuePercent * weight;
      totals[category].weightSum += weight;
    });

    // Extra signal: explicitly selecting "AI Agents" as a technology in Q2
    // gives the Agents category a direct boost, independent of the general
    // adoption-driven contribution above.
    if (q.id === 'q2' && Array.isArray(answer)) {
      const agentsOptionIndex = q.options.length - 1;
      if (answer.includes(agentsOptionIndex)) {
        totals.agents.weighted += 100 * 0.4;
        totals.agents.weightSum += 0.4;
      }
    }
  });

  const categoryScores = {};
  CATEGORIES.forEach((c) => {
    const t = totals[c.key];
    categoryScores[c.key] = t.weightSum > 0 ? Math.round(t.weighted / t.weightSum) : 0;
  });

  const averagePercent =
    CATEGORIES.reduce((sum, c) => sum + categoryScores[c.key], 0) / CATEGORIES.length;

  const overallScore = Math.min(Math.round(averagePercent * (MAX_SCORE / 100)), MAX_SCORE);

  return { categoryScores, overallScore };
}

export const MATURITY_LEVELS = [
  { max: 25, label: 'AI Beginner', color: '#D9433C' },
  { max: 50, label: 'AI Explorer', color: '#C9861A' },
  { max: 70, label: 'AI Adopter', color: '#B8A31A' },
  { max: 85, label: 'AI Optimized', color: '#1F9D63' },
  { max: Infinity, label: 'AI Leader', color: '#266CAC' },
];

export function getMaturityLevel(score) {
  return MATURITY_LEVELS.find((level) => score <= level.max);
}

// Shared category-level risk status, used by both the category cards and
// the area risk chart so the "Safe / Moderate / Critical" labeling and
// color-coding always stay in sync.
const STATUS_LEVELS = [
  { min: 70, label: 'Safe', color: '#1F9D63' },
  { min: 40, label: 'Moderate', color: '#C9861A' },
  { min: 0, label: 'Critical', color: '#D9433C' },
];

export function getCategoryStatus(score) {
  return STATUS_LEVELS.find((level) => score >= level.min);
}
